import React, { useEffect, useMemo, useState } from 'react';
import InventoryLayout from './InventoryLayout';

const OrdersList = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [confirmReplenish, setConfirmReplenish] = useState(null);
  const [sortBy, setSortBy] = useState('date_desc');

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const loadSales = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/inventory/usage', { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke hente salg');
      setSales(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Kunne ikke hente salg');
      setSales([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalAmount = useMemo(
    () => sales.reduce((sum, row) => sum + Number(row.qty || 0) * Number(row.unit_price || 0), 0),
    [sales]
  );

  const sortedSales = useMemo(() => {
    const rows = [...sales];
    rows.sort((a, b) => {
      const aPending = !a.replenished_at;
      const bPending = !b.replenished_at;
      if (aPending !== bPending) return aPending ? -1 : 1;
      const aDate = a.used_at ? new Date(a.used_at).getTime() : 0;
      const bDate = b.used_at ? new Date(b.used_at).getTime() : 0;
      if (sortBy === 'date_asc') return aDate - bDate;
      return bDate - aDate;
    });
    return rows;
  }, [sales, sortBy]);

  const openReplenish = (row) => {
    setConfirmReplenish({
      usage_id: row.id,
      item_id: row.item_id,
      item_name: row.item_name || '',
      qty: row.qty || 1
    });
  };

  const handleReplenishSubmit = async () => {
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/inventory/replenish', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          usage_id: confirmReplenish.usage_id,
          item_id: confirmReplenish.item_id,
          qty: confirmReplenish.qty
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke opdatere lager');
      setMessage('Lager opdateret.');
      setConfirmReplenish(null);
      loadSales();
    } catch (err) {
      setError(err.message || 'Kunne ikke opdatere lager');
    }
  };

  return (
    <InventoryLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Salg</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">Alle registrerede pluk (forbrug) fra lageret.</p>
        </div>
      </div>

      {error && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded">{error}</div>}
      {message && <div className="mb-3 p-3 bg-green-100 text-green-700 rounded">{message}</div>}

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <div className="text-sm text-gray-600 dark:text-gray-300">Afventer vises altid øverst.</div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            Sorter efter
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 dark:border-gray-700 rounded-md px-2 py-1 bg-white dark:bg-gray-900"
            >
              <option value="date_desc">Dato (nyeste først)</option>
              <option value="date_asc">Dato (ældste først)</option>
            </select>
          </label>
        </div>
        {loading ? (
          <div className="p-4 text-gray-500">Indlæser...</div>
        ) : sortedSales.length === 0 ? (
          <div className="p-4 text-gray-500">Ingen salg fundet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left">Dato</th>
                  <th className="px-3 py-2 text-left">Vogn nr.</th>
                  <th className="px-3 py-2 text-left">Vare</th>
                  <th className="px-3 py-2 text-left">Varenummer</th>
                  <th className="px-3 py-2 text-right">Antal</th>
                  <th className="px-3 py-2 text-right">Pris</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Handling</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {sortedSales.map((row) => {
                  const lineTotal = Number(row.qty || 0) * Number(row.unit_price || 0);
                  const isReplenished = Boolean(row.replenished_at);
                  const reorderUrl = (row.reorder_url || '').trim();
                  return (
                    <tr key={row.id}>
                      <td className="px-3 py-2">
                        {row.used_at ? new Date(row.used_at).toLocaleString('da-DK') : '—'}
                      </td>
                      <td className="px-3 py-2">{row.vogn_nr || '—'}</td>
                      <td className="px-3 py-2 font-medium">{row.item_name || '—'}</td>
                      <td className="px-3 py-2">{row.item_sku || '—'}</td>
                      <td className="px-3 py-2 text-right">{row.qty}</td>
                      <td className="px-3 py-2 text-right">{Number(row.unit_price || 0).toFixed(2)} kr</td>
                      <td className="px-3 py-2 text-right">{lineTotal.toFixed(2)} kr</td>
                      <td className="px-3 py-2">
                        {isReplenished ? (
                          <span className="text-green-700">Lagt på lager</span>
                        ) : (
                          <span className="text-gray-600">Afventer</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-3">
                          {reorderUrl && (
                            <a
                              href={reorderUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-falck-blue hover:underline text-sm"
                            >
                              Bestil
                            </a>
                          )}
                          {!isReplenished && (
                            <button
                              type="button"
                              onClick={() => openReplenish(row)}
                              className="text-falck-blue hover:underline text-sm"
                            >
                              Læg på lager
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="p-4 text-right text-sm text-gray-600 dark:text-gray-300">
              Total: <span className="font-semibold text-gray-900 dark:text-gray-100">{totalAmount.toFixed(2)} kr</span>
            </div>
          </div>
        )}
      </div>

      {confirmReplenish && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6 w-full max-w-md space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Læg på lager</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Har du lagt {confirmReplenish.qty} stk. {confirmReplenish.item_name} på lager?
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmReplenish(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md"
              >
                Nej
              </button>
              <button
                type="submit"
                onClick={handleReplenishSubmit}
                className="px-4 py-2 bg-falck-blue text-white rounded-md"
              >
                Ja
              </button>
            </div>
          </div>
        </div>
      )}
    </InventoryLayout>
  );
};

export default OrdersList;
